import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Square, Loader2, Sparkles } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import type { ChatMessage as ChatMessageType, PipelineOption } from '@/types/ai-pipeline';

interface AIChatPanelProps {
  messages: ChatMessageType[];
  isStreaming: boolean;
  isGenerating: boolean;
  hasDataSources: boolean;
  onSendMessage: (text: string) => void;
  onStopStreaming: () => void;
  onSelectPipeline: (option: PipelineOption) => void;
  onGeneratePipeline: (optionId?: string) => void;
}

export function AIChatPanel({
  messages,
  isStreaming,
  isGenerating,
  hasDataSources,
  onSendMessage,
  onStopStreaming,
  onSelectPipeline,
  onGeneratePipeline,
}: AIChatPanelProps) {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isStreaming || !hasDataSources) return;
    onSendMessage(input.trim());
    setInput('');
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectPipeline = (option: PipelineOption) => {
    if (isGenerating) return;
    onSelectPipeline(option);
    onGeneratePipeline(option.id);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">AI Pipeline Generator</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              {hasDataSources
                ? 'Describe what pipeline you want to build. The AI will analyze your datasources and suggest optimal pipeline configurations.'
                : 'Select at least one datasource from the left panel to start.'}
            </p>

            {hasDataSources && (
              <div className="mt-6 space-y-2 text-left w-full max-w-md">
                <p className="text-xs font-medium text-muted-foreground">Try this structured format:</p>
                {[
                  'objective: Analisis tren kemiskinan per wilayah\noutput_expected: dataset agregat tahunan per wilayah\nmust_use_fields: tahun,wilayah,nilai\nrules: filter tahun >= 2020, sum nilai by wilayah,tahun\nnormalization: cast tahun->NUMBER, nilai->NUMBER jika source JSON masih string\nconstraints: jangan invent field baru',
                  'objective: Bandingkan pertumbuhan antar kabupaten\noutput_expected: tabel ringkas per kabupaten per tahun\nmust_use_fields: tahun,kabupaten,nilai\nrules: filter nilai > 0, aggregate avg nilai by kabupaten,tahun\nnormalization: gunakan TRANSFORMATION awal untuk cast field numerik dari string\nconstraints: gunakan field schema yang tersedia saja',
                  'objective: Buat proyeksi 2 tahun ke depan\noutput_expected: proyeksi nilai per wilayah\nmust_use_fields: tahun,wilayah,nilai\nrules: gunakan projection linear 2 tahun, group by wilayah\nnormalization: pastikan tahun dan nilai di-cast NUMBER sebelum projection\nconstraints: output wajib valid untuk node SOURCE/FILTER/PROJECTION/DESTINATION',
                ].map((prompt, i) => (
                  <button
                    key={i}
                    className="w-full text-left text-sm p-3 rounded-lg border hover:bg-accent transition-colors"
                    onClick={() => {
                      setInput(prompt);
                      textareaRef.current?.focus();
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {messages.map((msg, i) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                onSelectPipeline={handleSelectPipeline}
                isStreaming={isStreaming && i === messages.length - 1 && msg.role === 'assistant'}
                isGenerating={isGenerating}
              />
            ))}
          </div>
        )}
      </div>

      <div className="border-t p-4">
        {isGenerating && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Loader2 className="h-4 w-4 animate-spin" />
            Generating pipeline configuration...
          </div>
        )}

        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              !hasDataSources
                ? 'Select a datasource first...'
                : isStreaming
                ? 'Waiting for AI response...'
                : 'Use format: objective / output_expected / must_use_fields / rules / normalization / constraints'
            }
            disabled={!hasDataSources || isStreaming || isGenerating}
            rows={2}
            className="resize-none flex-1"
          />

          {isStreaming ? (
            <Button
              variant="destructive"
              size="icon"
              onClick={onStopStreaming}
              className="shrink-0 self-end"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              size="icon"
              onClick={handleSend}
              disabled={!input.trim() || !hasDataSources || isGenerating}
              className="shrink-0 self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
